'use client'

import React, { useState, useEffect } from 'react'
import { Broker } from './types'
import styles from './ConnectorsPanel.module.css'
import AccountInfo from './AccountInfo'
import { Switch } from '@headlessui/react'
import { 
  useBrokers, 
  useConnectBroker, 
  useToggleBrokerEnabled, 
  useAutoConnect 
} from './hooks/useConnectorQueries'

const ConnectorsPanel: React.FC = () => {
  const [selectedBroker, setSelectedBroker] = useState<Broker | null>(null)
  const [credentials, setCredentials] = useState({ apiKey: '', apiSecret: '' })
  const [showAlert, setShowAlert] = useState(false)
  const [enabledBrokerName, setEnabledBrokerName] = useState('')

  // React Query hooks - no more useEffect!
  const { data: brokers = [], isLoading: brokersLoading, error: brokersError } = useBrokers()
  const connectBrokerMutation = useConnectBroker()
  const toggleBrokerMutation = useToggleBrokerEnabled()
  const autoConnectMutation = useAutoConnect()

  // Auto-connect on component mount (replaces the useEffect)
  React.useEffect(() => {
    autoConnectMutation.mutate()
  }, []) // Only run once on mount

  // Handle broker toggle with optimistic updates
  const handleToggleBrokerEnabled = (brokerId: string) => {
    const broker = brokers.find(b => b.id === brokerId)
    
    // If broker is being enabled (currently disabled), show alert
    if (broker && !broker.enabled) {
      setEnabledBrokerName(broker.name)
      setShowAlert(true)
    }
    
    toggleBrokerMutation.mutate(brokerId)
  }

  // Handle broker connection with error handling
  const handleConnect = async () => {
    if (!selectedBroker) return

    connectBrokerMutation.mutate(
      { brokerId: selectedBroker.id, credentials },
      {
        onSuccess: () => {
          // Clear form and close on success
          setCredentials({ apiKey: '', apiSecret: '' })
          setSelectedBroker(null)
        },
        onError: (error) => {
          // Show user-friendly error message
          alert(`Failed to connect to ${selectedBroker.name}: ${error.message}`)
    }
  }
    )
  }

  // Handle disconnection
  const handleDisconnect = () => {
    setSelectedBroker(null)
  }

  // Handle refresh action
  const handleRefresh = () => {
    setShowAlert(false)
    window.location.reload()
  }

  // Handle close alert
  const handleCloseAlert = () => {
    setShowAlert(false)
  }

  // Loading state
  if (brokersLoading) {
    return (
      <div className={styles.container}>
        <h2>Connect Your Broker</h2>
        <div className="flex items-center justify-center p-8">
          <div className="text-gray-500">Loading brokers...</div>
        </div>
      </div>
    )
  }

  // Error state
  if (brokersError) {
    return (
      <div className={styles.container}>
        <h2>Connect Your Broker</h2>
        <div className="flex items-center justify-center p-8">
          <div className="text-red-500">
            Error loading brokers: {brokersError.message}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <h2>Connect Your Broker</h2>

      <div className={styles.brokerGrid}>
        {brokers.map(broker => (
          <div
            key={broker.id}
            className={`${styles.brokerCard} ${broker.isConnected ? styles.connected : ''} ${!broker.enabled ? styles.disabled : ''}`}
          >
            <div
              className={styles.brokerContent}
              onClick={() => setSelectedBroker(broker)}
            >
              <img src={broker.logo} alt={broker.name} />
              <h3>{broker.name}</h3>
              <span className={styles.status}>
                {broker.isConnected ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            <div
              className={styles.toggleSwitch}
              onClick={e => e.stopPropagation()}
            >
              <Switch
                checked={broker.enabled}
                onChange={() => handleToggleBrokerEnabled(broker.id)}
                disabled={toggleBrokerMutation.isPending}
                className={`${broker.enabled ? styles.switchEnabled : styles.switchDisabled}`}
              >
                <span className={styles.switchHandle} />
              </Switch>
            </div>
          </div>
        ))}
      </div>

      {selectedBroker && selectedBroker.isConnected ? (
        <AccountInfo broker={selectedBroker} onDisconnect={handleDisconnect} />
      ) : (
        selectedBroker && (
          <div className={styles.credentialsForm}>
            <h3>Connect to {selectedBroker.name}</h3>
            <input
              type="text"
              placeholder="API Key"
              value={credentials.apiKey}
              onChange={e =>
                setCredentials(prev => ({ ...prev, apiKey: e.target.value }))
              }
              disabled={connectBrokerMutation.isPending}
            />
            {/* Only show API Secret input for brokers that require it (not Groww) */}
            {selectedBroker.id !== 'groww' && (
              <input
                type="password"
                placeholder="API Secret"
                value={credentials.apiSecret}
                onChange={e =>
                  setCredentials(prev => ({ ...prev, apiSecret: e.target.value }))
                }
                disabled={connectBrokerMutation.isPending}
              />
            )}
            <button 
              onClick={handleConnect}
              disabled={connectBrokerMutation.isPending || !credentials.apiKey}
            >
              {connectBrokerMutation.isPending ? 'Connecting...' : 'Connect'}
            </button>
            {connectBrokerMutation.error && (
              <div className="text-red-500 text-sm mt-2">
                Connection failed: {connectBrokerMutation.error.message}
              </div>
            )}
          </div>
        )
      )}

      {/* Alert Popup */}
      {showAlert && (
        <div className={styles.alertOverlay}>
          <div className={styles.alertModal}>
            <div className={styles.alertHeader}>
              <h3>Broker Enabled</h3>
              <button 
                className={styles.closeButton}
                onClick={handleCloseAlert}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className={styles.alertContent}>
              <p>{enabledBrokerName} has been successfully enabled.</p>
              <p>Would you like to refresh the page to see the latest updates?</p>
            </div>
            <div className={styles.alertActions}>
              <button 
                className={styles.refreshButton}
                onClick={handleRefresh}
              >
                Refresh
              </button>
              <button 
                className={styles.cancelButton}
                onClick={handleCloseAlert}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ConnectorsPanel
